import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DSData, DSSetup, Service } from 'webapp-types';
import { BlockVisualization, DataStoryType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import type { DSMap } from 'webapp/models-custom/block';
import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type MetricSetup from 'webapp/models-custom/metric-setup';
import VisualizationBase from 'webapp/models-custom/visualization/base';

const RecordLimit = 30;

export default class VisualizationDataStory extends VisualizationBase {
	@service('model-manager/data-selects')
	declare dataSelectsService: Service.ModelManager.DataSelects;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('data-story') declare dataStoryService: Service.DataStory;

	visualizationType = BlockVisualization.DATA_STORY;

	recordLimit = RecordLimit;

	defaultStoryType = DataStoryType.POSITIVITY_NEUTRAL;

	get shownText() {
		return this.block.text;
	}

	@cached
	get colorMap() {
		const dataSelects = this.blockSetup.sortedDataSelectsShown;

		const customColorClassMap = this.blockSetup.customColorClassMap;

		const colorMap: Record<string, number | string> = {}; // key is dsId

		const getDimensionValuesMapFromBasicFilters = (metricSetup: MetricSetup) => {
			// LOSSY CONVERSION BCOS IT ONLY TAKES FIRST BASIC FILTER
			const dimensionValues: DSData.DimensionsAndValuesMap = {};
			const filterGroup = metricSetup.dimensionFilterGroup,
				dimensions = metricSetup.dimensions;
			if (filterGroup) {
				filterGroup.filterItems.forEach((item) => {
					dimensions.forEach((dimension) => {
						if (item.checkIfIsBasicFilterForDimension(dimension)) {
							dimensionValues[dimension] = item.values[0] || '';
						}
					});
				});
			}
			return dimensionValues;
		};

		// specifically set colors
		dataSelects.forEach((ds) => {
			let color = this.blockSetup.getColorForMetricSelectionData({
				dsId: ds.numericId!,
				primaryDsId: null,
				metricSetupData: ds.metricSetupData,
				metricName: '',
				dimensionValues: getDimensionValuesMapFromBasicFilters(ds.metricSetup),
				unit: ds.unitName || '',
			});

			if (typeof color === 'string') {
				color = customColorClassMap[color] || null;
			}
			if (color !== null) {
				colorMap[ds.id!] = color;
			}
		});

		// see which colors are left for free using
		const usedColorIndexes: number[] = Object.values(colorMap)
			.filter((c) => typeof c === 'number')
			.map((c) => +c);
		const colorIndexesLeft = Array.from(this.blockSetup.allAvailableColorsIndexes).filter(
			(c) => !usedColorIndexes.includes(c),
		);

		const dataSelectsWithoutColor = dataSelects.filter((ds) => {
			const stringId = ds.id!;
			return typeof colorMap[stringId] === 'undefined';
		});

		const dispersedColorIndexes = this.getDispersedColors(
			colorIndexesLeft,
			dataSelectsWithoutColor.length,
		);

		dataSelectsWithoutColor.forEach((ds, idx) => {
			const color = dispersedColorIndexes[idx]!;
			colorMap[ds.id!] = color;
		});

		return colorMap;
	}

	override get maxPrimaryMainDataSelectsAllowed(): number {
		return this.recordLimit;
	}

	addMetric(): Promise<number> {
		return this.dataSelectsService
			.openMetricSelectWithAttributes({
				requireSingleOutput: true,
			})
			.then((metricSetupData: DSSetup.MetricSetupData) => {
				const ds = this.blockSetup.createPrimaryMainDataSelect({
					metricSetupKey: metricSetupData.key,
					metricSetupSpaceAccessId: metricSetupData.spaceAccessId,
					metricSetupDimensions: metricSetupData.dimensions,
					metricSetupFilters: metricSetupData.filters,
				});

				this.loggerService.logToConsole('[Vis data-story] Creating DS');

				return this.blockSetup.afterSaves().then(() => {
					const dsId = ds.numericId;
					if (dsId) {
						return dsId;
					} else {
						return Promise.reject();
					}
				});
			});
	}

	getNextAvailableStoryOrder(): number {
		let nextAvailable = 1; // start with 0 because of BE json numeric-key object parsing issues https://app.asana.com/0/0/1205335547058483/1205376383758525/f
		const usedStoryOrders = Object.keys(this.block.dsMap).map((o) => +o);
		while (usedStoryOrders.includes(nextAvailable)) {
			nextAvailable++;
		}
		return nextAvailable;
	}

	// ------- visualization switch helpers --------

	override onDeleteDataSelect(dataSelect: DataSelect): void {
		super.onDeleteDataSelect(dataSelect);

		const dsMap = this.block.dsMap,
			usedOrder = dataSelect.getStoryOrder();
		if (typeof usedOrder === 'number' && dsMap[usedOrder]) {
			// cleanup text in case user hasn't changed it from the default yet
			const block = this.block,
				hasCompare = dataSelect.compares.length <= 0;

			// check all story types
			const replaced = [
				this.defaultStoryType,
				DataStoryType.POSITIVITY_GOOD,
				DataStoryType.POSITIVITY_OK,
				DataStoryType.POSITIVITY_BAD,
			].some((type) => {
				const html = this.dataStoryService.prepareStoryHtmlForOrder(
					usedOrder,
					type,
					hasCompare,
				);
				if ((block.text || '').includes(html)) {
					block.text = block.text.replace(html, '');
					return true;
				}
				return false;
			});

			if (replaced) {
				this.eventManagerService.trigger(AppEvent.DESIGNER_DATA_STORY_OVERRIDE, {
					block,
				});
				this.loggerService.logToConsole('[Vis data-story] Deleting text from block');
			}

			// cleanup dsMap
			const newDsMap = Object.assign({}, dsMap); // force different reference for dirty
			delete newDsMap[usedOrder];
			this.block.dsMap = newDsMap;

			// save at the end
			this.blockSetup.saveBlock();
		}
	}

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// cleanup data-story specifics
		this.loggerService.logToConsole('[Vis change] from DataStory: cleanup text');
		this.block.text = '';
		this.block.dsMap = null;
	}

	override updateForSwitchFrom(visualizationFrom: AnyVisualization): void {
		super.updateForSwitchFrom(visualizationFrom);
		this.addContentAndDsMap(visualizationFrom);
	}

	addContentAndDsMap(visualizationFrom?: AnyVisualization) {
		let text = '';
		if (visualizationFrom?.isText) {
			text = visualizationFrom.block.text;
		}
		const dsMap: DSMap = {};

		this.blockSetup.dataSelectsShown.forEach((dataSelect, idx) => {
			const dsId = dataSelect.numericId;
			if (dsId) {
				const order = idx + 1; // start from 1 -- see above (getNextAvailableStoryOrder)
				const textForMetric = this.dataStoryService.prepareStoryHtmlForOrder(
					order,
					this.defaultStoryType,
					dataSelect.compares.length === 0,
				);
				text += textForMetric;
				dsMap[order] = dsId;
			}
		});
		// console.log(text);
		this.block.text = text;
		this.block.dsMap = dsMap;
	}

	addEmptyContent() {
		this.block.text = '';
		this.block.dsMap = null;
	}

	// ------- new visualization creation helpers -------

	override doAfterNewBlockCreatedActivities() {
		// at this point, the block will be already saved and have the necessary dataSelects
		this.addContentAndDsMap();

		this.eventManagerService.trigger(AppEvent.DESIGNER_DATA_STORY_OVERRIDE, {
			block: this.block,
		});

		return super.doAfterNewBlockCreatedActivities();
	}

	static override recordLimit = RecordLimit;
}
